import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaSearch, FaTimes, FaExclamationTriangle,
  FaFileContract, FaChevronDown, FaChevronRight,
} from "react-icons/fa";
import { API_BASE } from "../../api/config";
import FolgevertragModal from "./FolgevertragModal";
import VertragAbgleichModal from "./VertragAbgleichModal";
import KuendigenModal from "./KuendigenModal";

const API = API_BASE;

const fmt = (v) => {
  if (v == null) return "–";
  return Number(v).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
};
const fmtDate = (d) => {
  if (!d) return "–";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
  return d;
};

function StatusBadge({ status }) {
  const map = {
    aktiv:       { cls: "badge-green",  label: "Aktiv" },
    leerstand:   { cls: "badge-red",    label: "Leerstand" },
    endet_bald:  { cls: "badge-amber",  label: "Endet bald" },
    gekuendigt:  { cls: "badge-gray",   label: "Gekündigt" },
  };
  const s = map[status] || map.aktiv;
  return <span className={`me-badge ${s.cls}`}>{s.label}</span>;
}

const VORGANG_LABEL = { erfasst: "Vorgang erfasst", versendet: "Schreiben versendet", widerspruch: "Widerspruch",
  ausgezogen: "Ausgezogen", abgeschlossen: "Abgeschlossen" };
function VorgangChip({ status }) {
  if (!status) return null;
  return <span className="me-badge badge-violet" title="Kündigungsvorgang läuft">{VORGANG_LABEL[status] || status}</span>;
}

/* ────────────────────────────────────────────────────────────────────────── */
export default function Tab2Vertraege() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterObjekt, setFilterObjekt] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [suche, setSuche] = useState("");

  // Inline column filters
  const [colFilterMvNr, setColFilterMvNr] = useState("");
  const [colFilterMieter, setColFilterMieter] = useState("");
  const [colFilterStatus, setColFilterStatus] = useState("");
  const [expandedObj, setExpandedObj] = useState({});

  // Drawer
  const [drawer, setDrawer] = useState(null);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerZahlungen, setDrawerZahlungen] = useState(null);   // Welle 83: Monatsreihe aus daten_mz

  // Vertrags-Aktionen (Welle 44.D)
  const [auszugModal, setAuszugModal] = useState(null);
  const [folgevertragModal, setFolgevertragModal] = useState(null);   // {einheit}
  const [kuendigenModal, setKuendigenModal] = useState(null);         // {mietvertrag_nr, name}
  const [abgleichModal, setAbgleichModal] = useState(null);           // Welle 117: mietvertrag_nr
  const [aktionBusy, setAktionBusy] = useState(false);
  const [aktionFehler, setAktionFehler] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (filterObjekt) p.set("objekt", filterObjekt);
      if (filterStatus) p.set("status", filterStatus);
      if (suche) p.set("suche", suche);
      const res = await fetch(`${API}/mietverwaltung/api/vertraege?${p}`);
      const d = await res.json();
      if (d.status === "ok") setData(d);
    } catch (e) { console.error("Vertraege:", e); }
    setLoading(false);
  }, [filterObjekt, filterStatus, suche]);

  useEffect(() => { loadData(); }, [loadData]);

  // Drawer
  const openDrawer = async (v) => {
    setDrawer(v);
    setDrawerLoading(true);
    try {
      const res = await fetch(`${API}/mietverwaltung/api/vertrag-detail?mietvertrag_nr=${encodeURIComponent(v.mietvertrag_nr)}`);
      const d = await res.json();
      if (d.status === "ok") setDrawerData(d);
      fetch(`${API}/mietverwaltung/api/zahlungen-auto/vertrag?mietvertrag_nr=${encodeURIComponent(v.mietvertrag_nr)}&monate=12`)
        .then((r) => r.json()).then((z) => setDrawerZahlungen(z.monate || [])).catch(() => setDrawerZahlungen([]));
    } catch (e) { console.error("Detail:", e); }
    setDrawerLoading(false);
  };
  const closeDrawer = () => { setDrawer(null); setDrawerData(null); setDrawerZahlungen(null); setAusPdf(null); };

  // Welle 109.1: Mieter-Stammdaten aus dem Mietvertrags-PDF im Vertragsordner ergänzen
  const [ausPdf, setAusPdf] = useState(null);     // {busy, vorschau, fehler, ergebnis}
  const ausPdfLaden = async (speichern) => {
    const nr = drawer?.mietvertrag_nr;
    if (!nr) return;
    setAusPdf((a) => ({ ...(a || {}), busy: true, fehler: "" }));
    try {
      const res = await fetch(`${API}/mietverwaltung/api/vertrag/${encodeURIComponent(nr)}/stammdaten-aus-pdf`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(speichern ? { speichern: true, mieter: ausPdf?.vorschau?.mieter } : {}) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "PDF konnte nicht gelesen werden");
      if (speichern) {
        setAusPdf({ ergebnis: d });
        openDrawer(drawer);
      } else {
        setAusPdf({ vorschau: d });
      }
    } catch (e) { setAusPdf((a) => ({ ...(a || {}), busy: false, fehler: String(e.message || e) })); }
  };

  // ── Vertrags-Aktionen (Welle 44.D) ──────────────────────────────────────
  const oeffneNeuModal = (v) => {
    setAktionFehler("");
    // Bei Einheiten ohne Vertrag ist die MV-Nr die Einheit selbst (z. B. „TST-02")
    setFolgevertragModal({
      einheit: v.einheit || (v.mietvertrag_nr || "").slice(0, 6),
      erstvertrag: !!v.ohne_vertrag,
      qm: v.qm,
    });
  };

  // Welle 106: Vertragsfelder ändern
  const [bearbeiten, setBearbeiten] = useState(null);      // {felder:{}} oder null
  const vertragSpeichern = async () => {
    if (!bearbeiten || !drawer) return;
    setAktionBusy(true); setAktionFehler("");
    try {
      const res = await fetch(`${API}/mietverwaltung/api/vertrag/${encodeURIComponent(drawer.mietvertrag_nr)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ felder: bearbeiten }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Speichern fehlgeschlagen");
      setBearbeiten(null);
      await openDrawer(drawer);
      loadData();
    } catch (e) { setAktionFehler(String(e.message || e)); }
    setAktionBusy(false);
  };

  // Welle 106: Mietvertrag als PDF aus den Vertragsdaten
  const [vertragBusy, setVertragBusy] = useState("");
  const mietvertragErzeugen = async (nr) => {
    setVertragBusy(nr); setAktionFehler("");
    try {
      const res = await fetch(`${API}/mietverwaltung/api/vertrag/${encodeURIComponent(nr)}/mietvertrag?ablegen=1`,
        { method: "POST" });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || "Mietvertrag konnte nicht erzeugt werden");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      const pfad = res.headers.get("X-Ablage-Pfad");
      setAktionFehler(pfad ? `Abgelegt: ${decodeURIComponent(pfad)}` : "");
    } catch (e) { setAktionFehler(String(e.message || e)); }
    setVertragBusy("");
  };

  const submitAuszug = async () => {
    if (!auszugModal?.auszug) { setAktionFehler("Bitte Auszugsdatum angeben."); return; }
    setAktionBusy(true); setAktionFehler("");
    try {
      const res = await fetch(`${API}/mietverwaltung/api/vertrag/auszug`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mietvertrag_nr: auszugModal.mietvertrag_nr,
          auszug: auszugModal.auszug,
          gekuendigt_am: auszugModal.gekuendigt_am || null,
          kuendigung_zum: auszugModal.kuendigung_zum || null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Speichern fehlgeschlagen");
      setAuszugModal(null);
      closeDrawer();
      loadData();
    } catch (e) { setAktionFehler(String(e.message || e)); }
    setAktionBusy(false);
  };

  const modalBox = { background: "#1e293b", border: "1px solid #475569", borderRadius: 12,
    padding: 20, width: "min(480px, 92vw)", maxHeight: "85vh", overflowY: "auto" };
  const modalGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 14px", marginTop: 12 };
  const modalLabel = { display: "flex", flexDirection: "column", gap: 4, fontSize: 13, color: "#94a3b8" };
  const modalInput = { height: 34, border: "1px solid #475569", borderRadius: 7, padding: "0 10px",
    background: "#334155", color: "#f1f5f9", fontSize: 13.5 };

  // Gruppierung nach Objekt (mit inline column filter)
  const grouped = React.useMemo(() => {
    if (!data?.vertraege) return {};
    let rows = data.vertraege;
    if (colFilterMvNr) {
      const f = colFilterMvNr.toLowerCase();
      rows = rows.filter(v => (v.mietvertrag_nr || "").toLowerCase().includes(f));
    }
    if (colFilterMieter) {
      const f = colFilterMieter.toLowerCase();
      rows = rows.filter(v => (v.name || "").toLowerCase().includes(f));
    }
    if (colFilterStatus) {
      rows = rows.filter(v => v.status === colFilterStatus);
    }
    const g = {};
    for (const v of rows) {
      const obj = v.objekt || "–";
      if (!g[obj]) g[obj] = [];
      g[obj].push(v);
    }
    return g;
  }, [data, colFilterMvNr, colFilterMieter, colFilterStatus]);

  const toggleObj = (obj) => setExpandedObj((p) => ({ ...p, [obj]: !p[obj] }));

  const kpis = data?.kpis || {};
  const warnungen = data?.warnungen || [];
  const objOptionen = data?.objekt_optionen || [];

  if (loading && !data) {
    return (
      <div className="me-loading">
        <div className="me-spinner" />
        <p>Verträge werden geladen…</p>
      </div>
    );
  }

  return (
    <>
      {/* ── KPI Cards (nur Objekte, Einheiten, Aktive, Leerstand) ── */}
      <div className="me-kpis me-kpis-small">
        <div className="me-kpi-card">
          <span className="me-kpi-label">Objekte</span>
          <span className="me-kpi-value blue">{kpis.objekte ?? 0}</span>
        </div>
        <div className="me-kpi-card">
          <span className="me-kpi-label">Einheiten</span>
          <span className="me-kpi-value blue">{kpis.einheiten ?? 0}</span>
        </div>
        <div className="me-kpi-card kpi-bezahlt">
          <span className="me-kpi-label">Aktive Verträge</span>
          <span className="me-kpi-value green">{kpis.aktive ?? 0}</span>
        </div>
        <div className="me-kpi-card kpi-offen">
          <span className="me-kpi-label">Leerstand</span>
          <span className="me-kpi-value red">{kpis.leerstand ?? 0}</span>
        </div>
      </div>

      {/* ── Warnungen mit Details ── */}
      {warnungen.length > 0 && (
        <div className="me-warning-section">
          {warnungen.map((w, i) => (
            <div key={i} className="me-warning-block">
              <div className="me-warning-item">
                <FaExclamationTriangle />
                <span>{w.text}</span>
              </div>
              {w.details && w.details.length > 0 && (
                <div className="me-warning-details">
                  {w.details.map((d, j) => (
                    <span key={j} className="me-warning-detail-item">
                      {d.mietvertrag_nr || d.mietvertrag} – {d.name}
                      {d.vertrag_ende && ` (bis ${fmtDate(d.vertrag_ende)})`}
                      {d.gekuendigt_am && ` (gek. ${fmtDate(d.gekuendigt_am)})`}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* ── Filter ── */}
      <div className="me-table-section">
        <div className="me-table-header">
          <h3>
            <FaFileContract style={{ marginRight: 6 }} />
            Einheiten & Verträge
            {data?.vertraege && ` (${data.vertraege.length})`}
          </h3>
          <div className="me-filters">
            <select className="me-select me-select-sm" value={filterObjekt}
              onChange={(e) => setFilterObjekt(e.target.value)}>
              <option value="">Alle Objekte</option>
              {objOptionen.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <select className="me-select me-select-sm" value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="">Alle Status</option>
              <option value="aktiv">🟢 Aktiv</option>
              <option value="leerstand">🔴 Leerstand</option>
              <option value="endet_bald">🟡 Endet bald</option>
              <option value="gekuendigt">⚪ Gekündigt</option>
            </select>
            <div className="me-search-wrap">
              <FaSearch className="me-search-icon" />
              <input
                className="me-filter-input"
                placeholder="Suche…"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Tabelle ── */}
        <div className="me-table-wrap">
          <table className="me-table">
            <thead>
              <tr>
                <th style={{ width: 30 }}></th>
                <th>MV-Nr</th>
                <th>Mieter</th>
                <th>Status</th>
                <th className="num">NKM</th>
                <th className="num">€/qm</th>
                <th>NKM ab</th>
                <th className="num">Überweisung</th>
                <th>Beginn</th>
                <th>Ende</th>
              </tr>
              <tr className="me-filter-row">
                <th></th>
                <th><input className="me-col-filter" placeholder="Filter…" value={colFilterMvNr} onChange={e => setColFilterMvNr(e.target.value)} /></th>
                <th><input className="me-col-filter" placeholder="Filter…" value={colFilterMieter} onChange={e => setColFilterMieter(e.target.value)} /></th>
                <th>
                  <select className="me-col-filter" value={colFilterStatus} onChange={e => setColFilterStatus(e.target.value)}>
                    <option value="">Alle</option>
                    <option value="aktiv">Aktiv</option>
                    <option value="leerstand">Leerstand</option>
                    <option value="endet_bald">Endet bald</option>
                    <option value="gekuendigt">Gekündigt</option>
                  </select>
                </th>
                <th></th><th></th><th></th><th></th><th></th><th></th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(grouped).length === 0 ? (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>
                    Keine Verträge gefunden.
                  </td>
                </tr>
              ) : (
                Object.entries(grouped).map(([obj, vertraege]) => (
                  <React.Fragment key={obj}>
                    <tr className="me-group-row" onClick={() => toggleObj(obj)}>
                      <td>{expandedObj[obj] === false ? <FaChevronRight /> : <FaChevronDown />}</td>
                      <td colSpan={9}>
                        <strong>Objekt {obj}</strong>
                        <span className="me-group-count">{vertraege.length} Einheiten</span>
                      </td>
                    </tr>
                    {expandedObj[obj] !== false && vertraege.map((v) => (
                      <tr key={v.mietvertrag_nr} className="me-mv-row"
                          onClick={() => (v.ohne_vertrag ? oeffneNeuModal(v) : openDrawer(v))}
                          title={v.ohne_vertrag ? "Einheit ohne Vertrag — klicken, um zu vermieten" : undefined}>
                        <td></td>
                        <td className="me-mv-id">{v.mietvertrag_nr}</td>
                        <td>{v.name}{v.ohne_vertrag && (
                          <small style={{ color: "#94a3b8" }}> · noch kein Vertrag</small>
                        )}</td>
                        <td><StatusBadge status={v.status} /> <VorgangChip status={v.kuendigung_status} /></td>
                        <td className="num">{fmt(v.aktuelle_nkm)}</td>
                        <td className="num">{v.nkm_qm != null ? Number(v.nkm_qm).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "–"}</td>
                        <td>{fmtDate(v.nkm_gueltig_ab)}</td>
                        <td className="num">{fmt(v.aktueller_ueberw)}</td>
                        <td>{fmtDate(v.vertrag_beginn)}</td>
                        <td>{v.ohne_vertrag ? "–" : (v.vertrag_ende ? fmtDate(v.vertrag_ende) : "unbefristet")}
                          {/* Welle 106: bei Leerstand direkt neu vermieten — ohne Umweg über den Drawer */}
                          {v.status === "leerstand" && (
                            <button className="me-btn me-btn-outline"
                                    style={{ marginLeft: 8, padding: "2px 8px", fontSize: 12 }}
                                    title="Neuen Mietvertrag für diese Einheit anlegen"
                                    onClick={(e) => { e.stopPropagation(); oeffneNeuModal(v); }}>
                              neu vermieten
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Drawer ── */}
      {drawer && (
        <div className="me-drawer-overlay" onClick={closeDrawer}>
          <div className="me-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="me-drawer-header">
              <div>
                <h3>{drawer.name || drawer.mietvertrag_nr}</h3>
                <span className="me-drawer-sub">
                  {drawer.mietvertrag_nr} · Objekt {drawer.objekt}
                  {drawer.einheit && ` · Einheit ${drawer.einheit}`}
                </span>
              </div>
              <button className="me-btn me-btn-icon" onClick={closeDrawer}><FaTimes /></button>
            </div>

            {drawerLoading ? (
              <div className="me-loading"><div className="me-spinner" /><p>Lade Details…</p></div>
            ) : drawerData ? (
              <div className="me-drawer-body">
                {/* Vertragsdaten */}
                {drawerData.vertrag && (
                  <div className="me-drawer-section">
                    <h4>
                      Vertragsdaten
                      <button className="me-btn me-btn-outline" style={{ marginLeft: 10, padding: "2px 8px", fontSize: 12 }}
                        onClick={() => setBearbeiten(bearbeiten ? null : {
                          kaution: drawerData.vertrag.kaution ?? "",
                          anzahl_personen: drawerData.vertrag.anzahl_personen ?? "",
                          vertrag_beginn: (drawerData.vertrag.vertrag_beginn || "").slice(0, 10),
                          vertrag_ende: (drawerData.vertrag.vertrag_ende || "").slice(0, 10),
                          einzug: (drawerData.vertrag.einzug || "").slice(0, 10),
                          garage_nr: drawerData.vertrag.garage_nr ?? "",
                          unser_zeichen: drawerData.vertrag.unser_zeichen ?? "",
                        })}>
                        {bearbeiten ? "Abbrechen" : "Bearbeiten"}
                      </button>
                    </h4>
                    {bearbeiten && (
                      <div className="me-drawer-grid" style={{ marginBottom: 10 }}>
                        {[["Vertragsbeginn", "vertrag_beginn", "date"], ["Vertragsende", "vertrag_ende", "date"],
                          ["Einzug", "einzug", "date"], ["Kaution €", "kaution", "text"],
                          ["Personen", "anzahl_personen", "number"], ["Garage/Stellplatz", "garage_nr", "text"],
                          ["Unser Zeichen", "unser_zeichen", "text"]].map(([label, key, typ]) => (
                          <React.Fragment key={key}>
                            <span>{label}:</span>
                            <input type={typ} className="me-input" value={bearbeiten[key] ?? ""}
                              onChange={(e) => setBearbeiten((b) => ({ ...b, [key]: e.target.value }))} />
                          </React.Fragment>
                        ))}
                        <span />
                        <button className="me-btn me-btn-primary" disabled={aktionBusy} onClick={vertragSpeichern}>
                          {aktionBusy ? "Speichert…" : "Speichern"}
                        </button>
                      </div>
                    )}
                    <div className="me-drawer-grid">
                      <span>Mieter:</span>
                      <span>{drawerData.vertrag.mieter1_name || drawerData.vertrag.mv_kurzname || "–"}</span>
                      <span>Vertrag:</span>
                      <span>{fmtDate(drawerData.vertrag.vertrag_beginn)} – {drawerData.vertrag.vertrag_ende ? fmtDate(drawerData.vertrag.vertrag_ende) : "unbefristet"}</span>
                      <span>Fläche:</span>
                      <span>{drawerData.vertrag.qm ? `${Number(drawerData.vertrag.qm).toLocaleString("de-DE")} qm` : "–"}</span>
                      <span>Kaution:</span>
                      <span>{drawerData.vertrag.kaution ? fmt(drawerData.vertrag.kaution) : "–"}</span>
                      <span>Personen:</span>
                      <span>{drawerData.vertrag.anzahl_personen || "–"}</span>
                      <span>DMR:</span>
                      <span>{drawerData.vertrag.dauermietrechnung || "–"}</span>
                      <span>Unser Zeichen:</span>
                      <span>{drawerData.vertrag.unser_zeichen || "–"}</span>
                    </div>
                  </div>
                )}

                {/* Welle 106: Mieter-Stammdaten (aus „adressen") */}
                {(drawerData.mieter || []).length > 0 && (
                  <div className="me-drawer-section">
                    <h4 style={{ display: "flex", alignItems: "center", gap: 8 }}>Mieter
                      {drawerData.mieter.some((m) => (m.fehlend || []).length) && !ausPdf?.vorschau && (
                        <button className="me-btn me-btn-outline" style={{ padding: "2px 8px", fontSize: 12 }}
                          disabled={ausPdf?.busy} onClick={() => setAbgleichModal(drawer.mietvertrag_nr)}
                          title="Mietvertrag lesen (Scan oder PDF aus dem Vertragsordner) und fehlende Angaben übernehmen">
                          {ausPdf?.busy ? "liest PDF…" : "📄 Aus Mietvertrag übernehmen"}</button>
                      )}
                    </h4>
                    {ausPdf?.fehler && <p style={{ color: "#fca5a5", fontSize: 12.5 }}>{ausPdf.fehler}</p>}
                    {ausPdf?.ergebnis && (
                      <p style={{ color: "#86efac", fontSize: 12.5 }}>Übernommen aus „{ausPdf.ergebnis.datei?.name}“
                        {(ausPdf.ergebnis.ergaenzt || []).length ? `: ${ausPdf.ergebnis.ergaenzt.map((z) => z.replace(/\b(anrede|vorname|geb_datum|beruf|strasse|plz_ort|mobil|telefon|mail|iban)\b/g,
                          (k) => ({ anrede: "Anrede", vorname: "Vorname", geb_datum: "Geburtsdatum", beruf: "Beruf", strasse: "bisherige Straße",
                                    plz_ort: "bisheriger Ort", mobil: "Mobil", telefon: "Telefon", mail: "E-Mail", iban: "IBAN" }[k]))).join(" · ")}` : " — es war schon alles da."}</p>
                    )}
                    {ausPdf?.vorschau && (
                      <div style={{ border: "1px solid #a16207", background: "#1c1917", borderRadius: 8, padding: 10,
                                    marginBottom: 10, fontSize: 12.5 }}>
                        <div style={{ color: "#fde68a", marginBottom: 6 }}>
                          Gelesen aus <b>{ausPdf.vorschau.datei?.name}</b>
                          {!ausPdf.vorschau.passt && <span style={{ color: "#fca5a5" }}> — Achtung: die Namen passen nicht zum Vertrag!</span>}
                        </div>
                        {(ausPdf.vorschau.mieter || []).map((m, i) => (
                          <div key={i} style={{ marginBottom: 6 }}>
                            <b>{[m.anrede, m.vorname, m.name].filter(Boolean).join(" ")}</b>
                            {m.geb_datum ? ` · geb. ${fmtDate(m.geb_datum)}` : ""}{m.beruf ? ` · ${m.beruf}` : ""}
                            <div style={{ color: "#94a3b8" }}>
                              {[m.strasse && `bisher: ${[m.strasse, m.plz_ort].filter(Boolean).join(", ")}`,
                                m.mobil || m.telefon, m.mail].filter(Boolean).join(" · ")}
                            </div>
                          </div>
                        ))}
                        {(ausPdf.vorschau.hinweise || []).map((h, i) => <div key={i} style={{ color: "#fbbf24" }}>{h}</div>)}
                        <div style={{ color: "#94a3b8", margin: "6px 0" }}>
                          Gefüllt werden nur leere Felder; die Anschrift wird die der Wohnung, die bisherige bleibt als „bisherige Anschrift“ erhalten.
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button className="me-btn me-btn-primary" style={{ padding: "3px 10px", fontSize: 12.5 }}
                            disabled={ausPdf.busy} onClick={() => ausPdfLaden(true)}>{ausPdf.busy ? "speichert…" : "Übernehmen"}</button>
                          <button className="me-btn me-btn-outline" style={{ padding: "3px 10px", fontSize: 12.5 }}
                            onClick={() => setAusPdf(null)}>Abbrechen</button>
                        </div>
                      </div>
                    )}
                    {drawerData.mieter.map((m) => (
                      <div key={m.pos} style={{ marginBottom: 10 }}>
                        <div className="me-drawer-grid">
                          <span>Name:</span>
                          {/* Der Name aus dem Vertrag enthält den Vornamen oft schon */}
                          <span>{[m.anrede,
                                  m.vorname && !String(m.name || "").startsWith(m.vorname) ? m.vorname : null,
                                  m.name].filter(Boolean).join(" ") || "–"}</span>
                          <span>Geburtsdatum:</span><span>{m.geb_datum ? fmtDate(m.geb_datum) : "–"}</span>
                          <span>Beruf:</span><span>{m.beruf || "–"}</span>
                          <span>Anschrift:</span><span>{[m.strasse, m.plz_ort].filter(Boolean).join(", ") || "–"}</span>
                          {m.alte_strasse && <><span>bisher:</span><span>{[m.alte_strasse, m.alte_plz_ort].filter(Boolean).join(", ")}</span></>}
                          <span>Telefon:</span><span>{m.mobil || m.telefon_privat || "–"}</span>
                          <span>E-Mail:</span><span>{m.mail || "–"}</span>
                        </div>
                        {(m.fehlend || []).length > 0 && (
                          <p style={{ color: "#fbbf24", fontSize: 12.5, margin: "4px 0 0" }}>
                            Es fehlen noch: {m.fehlend.map((f) => ({
                              geb_datum: "Geburtsdatum", strasse: "Straße", plz_ort: "PLZ/Ort", mail: "E-Mail",
                            }[f] || f)).join(", ")}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Welle 106: Dokumente zum Vertrag */}
                {(drawerData.dokumente || []).length > 0 && (
                  <div className="me-drawer-section">
                    <h4>Dokumente ({drawerData.dokumente.length})</h4>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                      {drawerData.dokumente.map((d) => (
                        <li key={d.id} style={{ marginBottom: 4 }}>
                          <span style={{ color: "#94a3b8" }}>{d.richtung === "eingehend" ? "↓" : "↑"}</span>{" "}
                          {d.dateiname}
                          {d.dropbox_pfad ? <small style={{ color: "#64748b" }}> · abgelegt</small> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Aktuelle Konditionen */}
                {drawerData.miethistorie?.length > 0 && (
                  <div className="me-drawer-section">
                    <h4>Aktuelle Konditionen</h4>
                    {(() => {
                      const h = drawerData.miethistorie[0];
                      return (
                        <div className="me-drawer-grid">
                          <span>NKM:</span><span>{fmt(h.nettokaltmiete)}</span>
                          <span>NK-VZ BK:</span><span>{fmt(h.vz_betriebskosten)}</span>
                          <span>NK-VZ HK:</span><span>{fmt(h.vz_heizkosten)}</span>
                          <span>MWSt:</span><span>{fmt(h.mwst)}</span>
                          <span>Brutto:</span><span><strong>{fmt(h.brutto_miete)}</strong></span>
                          <span>Überweisung:</span><span><strong>{fmt(h.ueberweisungsbetrag)}</strong></span>
                          <span>€/qm:</span><span>{h.nkm_qm != null ? `${Number(h.nkm_qm).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €` : "–"}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Miethistorie */}
                {drawerData.miethistorie?.length > 0 && (
                  <div className="me-drawer-section">
                    <h4>Miethistorie ({drawerData.miethistorie.length})</h4>
                    <div className="me-drawer-table-wrap">
                      <table className="me-table me-table-compact">
                        <thead>
                          <tr>
                            <th>Ab</th>
                            <th>Dokument</th>
                            <th className="num">NKM</th>
                            <th className="num">Überw.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drawerData.miethistorie.map((h, i) => (
                            <tr key={i}>
                              <td>{fmtDate(h.miete_ab)}</td>
                              <td>{h.dokument || "–"}</td>
                              <td className="num">{fmt(h.nettokaltmiete)}</td>
                              <td className="num">{fmt(h.ueberweisungsbetrag)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Alle Verträge der Einheit */}
                {drawerData.alle_vertraege?.length > 1 && (
                  <div className="me-drawer-section">
                    <h4>Vertragshistorie Einheit ({drawerData.alle_vertraege.length})</h4>
                    <div className="me-drawer-table-wrap">
                      <table className="me-table me-table-compact">
                        <thead>
                          <tr>
                            <th>MV-Nr</th>
                            <th>Mieter</th>
                            <th>Beginn</th>
                            <th>Ende</th>
                          </tr>
                        </thead>
                        <tbody>
                          {drawerData.alle_vertraege.map((v, i) => (
                            <tr key={i} className={v.mietvertrag_nr === drawer.mietvertrag_nr ? "me-row-highlight" : ""}>
                              <td className="me-mv-id">{v.mietvertrag_nr}</td>
                              <td>{v.name}</td>
                              <td>{fmtDate(v.vertrag_beginn)}</td>
                              <td>{v.vertrag_ende ? fmtDate(v.vertrag_ende) : "unbefristet"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Welle 83: Zahlungen der letzten 12 Monate (Bank-Umsätze → daten_mz) */}
                <div className="me-drawer-section">
                  <h4>Zahlungen (12 Monate)</h4>
                  {!drawerZahlungen ? <p style={{ fontSize: 13, color: "#94a3b8" }}>Lädt…</p>
                    : drawerZahlungen.length === 0 ? <p style={{ fontSize: 13, color: "#94a3b8" }}>Keine Soll-/Ist-Zeilen — Umsätze werden täglich aus der Bank zugeordnet (Tab Zahlungen → Automatik).</p>
                    : (
                    <table className="me-table me-table-compact">
                      <thead><tr><th>Monat</th><th className="num">Soll</th><th className="num">Ist</th><th className="num">Offen</th><th className="num">Kumuliert</th><th>Zahlung</th></tr></thead>
                      <tbody>
                        {drawerZahlungen.map((z) => (
                          <tr key={`${z.jahr}-${z.monat}`} title={z.zweck || ""}>
                            <td>{String(z.monat).padStart(2, "0")}/{z.jahr}</td>
                            <td className="num">{fmt(z.soll)}</td>
                            <td className={`num ${z.ist > 0 ? "pos" : ""}`}>{fmt(z.ist)}</td>
                            <td className={`num ${z.offen > 0.01 ? "neg" : ""}`}>{fmt(z.offen)}</td>
                            <td className={`num ${z.kumuliert < -0.01 ? "neg" : ""}`}>{fmt(z.kumuliert)}</td>
                            <td style={{ fontSize: "0.78rem", color: "#94a3b8" }}>{z.zahldatum ? fmtDate(z.zahldatum) : "–"}{z.quelle ? ` · ${z.quelle}` : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Zahlungsstatus aktueller Monat */}
                {drawerData.zahlungen_aktuell?.length > 0 && (
                  <div className="me-drawer-section">
                    <h4>Zahlungsstatus {drawerData.aktuell?.monat}/{drawerData.aktuell?.jahr}</h4>
                    <table className="me-table me-table-compact">
                      <thead>
                        <tr>
                          <th>Typ</th>
                          <th className="num">Soll</th>
                          <th className="num">Ist</th>
                          <th className="num">OPOS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drawerData.zahlungen_aktuell.map((z, i) => {
                          const diff = (z.ist || 0) - (z.soll || 0);
                          return (
                          <tr key={i}>
                            <td>{z.typ}</td>
                            <td className="num">{fmt(z.soll)}</td>
                            <td className="num">{fmt(z.ist)}</td>
                            <td className={`num ${diff < -0.01 ? "neg" : ""}`}>{fmt(diff)}</td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Index-Überwachung */}
                {drawerData.index && (
                  <div className="me-drawer-section">
                    <h4>Index-Überwachung</h4>
                    <div className="me-drawer-grid">
                      <span>Wertsicherung:</span><span>{drawerData.index.paragraf_wertsicherung || "–"}</span>
                      <span>Laufzeit:</span><span>{drawerData.index.paragraf_index_laufzeit || "–"}</span>
                      <span>Überwachung:</span><span>{drawerData.index.index_ueberwachung || "–"}</span>
                      <span>Start:</span><span>{fmtDate(drawerData.index.index_start_datum)}</span>
                      <span>Basis:</span><span>{drawerData.index.index_basis_wert ?? "–"}</span>
                      <span>Fest Monate:</span><span>{drawerData.index.laufzeit_miete_fest || "–"}</span>
                      <span>Fest bis:</span><span>{fmtDate(drawerData.index.miete_fest_bis)}</span>
                      <span>SOLL Veränd.:</span><span>{drawerData.index.soll_index_veraenderung != null ? `${(Number(drawerData.index.soll_index_veraenderung) * 100).toFixed(1)}%` : "–"}</span>
                      <span>IST Veränd.:</span><span>{drawerData.index.ist_index_veraenderung != null ? `${(Number(drawerData.index.ist_index_veraenderung) * 100).toFixed(1)}%` : "–"}</span>
                      <span>Anpassung:</span><span>{drawerData.index.index_prozent_anpassung != null ? `${(Number(drawerData.index.index_prozent_anpassung) * 100).toFixed(0)}%` : "–"}</span>
                    </div>
                  </div>
                )}
              </div>
            ) : null}

            <div className="me-drawer-footer me-drawer-actions">
              {(() => {
                const zeile = (data?.vertraege || []).find((x) => x.mietvertrag_nr === drawer.mietvertrag_nr);
                const kid = zeile?.kuendigung_id;
                return kid ? (
                  <button className="me-btn me-btn-danger" title="Laufenden Kündigungsvorgang öffnen"
                    onClick={() => navigate(`/mietverwaltung?tab=vorgaenge&vorgang=${kid}`)}>
                    Vorgang öffnen
                  </button>
                ) : (
                  <button className="me-btn me-btn-danger" title="Kündigungsvorgang starten (Mieter oder Vermieter kündigt)"
                    onClick={() => { setAktionFehler(""); setKuendigenModal({ mietvertrag_nr: drawer.mietvertrag_nr, name: drawer.name }); }}>
                    Kündigen
                  </button>
                );
              })()}
              <button className="me-btn me-btn-outline" title="Auszug direkt erfassen (ohne Vorgang)"
                onClick={() => { setAktionFehler(""); setAuszugModal({
                  mietvertrag_nr: drawer.mietvertrag_nr,
                  auszug: "", gekuendigt_am: "", kuendigung_zum: "" }); }}>
                Auszug
              </button>
              <button className="me-btn me-btn-primary" title="Neuen Folgevertrag für diese Einheit anlegen"
                onClick={() => oeffneNeuModal(drawer)}>
                Folgevertrag
              </button>
              {(() => {
                // Welle 106: Einzugsvorgang zum Vertrag (entsteht beim Anlegen automatisch)
                const zeile = (data?.vertraege || []).find((x) => x.mietvertrag_nr === drawer.mietvertrag_nr);
                return zeile?.einzug_id ? (
                  <button className="me-btn me-btn-outline" title="Einzug: Checkliste und Dokumente"
                    onClick={() => navigate(`/mietverwaltung?tab=vorgaenge&vorgang=${zeile.einzug_id}`)}>
                    Einzug öffnen
                  </button>
                ) : null;
              })()}
              <button className="me-btn me-btn-outline"
                title="Unterschriebenen Mietvertrag einscannen bzw. das PDF lesen, mit den Stammdaten vergleichen und Abweichungen übernehmen"
                onClick={() => setAbgleichModal(drawer.mietvertrag_nr)}>
                Mietvertrag abgleichen
              </button>
              <button className="me-btn me-btn-outline" title="Mietvertrag aus den Vertragsdaten erzeugen und ablegen"
                disabled={vertragBusy === drawer.mietvertrag_nr}
                onClick={() => mietvertragErzeugen(drawer.mietvertrag_nr)}>
                {vertragBusy === drawer.mietvertrag_nr ? "Erzeuge…" : "Mietvertrag erzeugen"}
              </button>
              <button className="me-btn me-btn-outline" title="Dokumente für diesen Vertrag (Bescheinigungen, Kündigung, Übergabe, Kaution)"
                onClick={() => navigate(`/mietverwaltung?tab=bescheinigungen&vertrag=${drawer.mietvertrag_nr}`)}>
                Dokumente
              </button>
              <button className="me-btn me-btn-outline" title="Übergabeprotokoll (Einzug oder Auszug) — aus der Datenbank vorbelegt"
                onClick={() => navigate(`/mietverwaltung?tab=bescheinigungen&doc=uebergabeprotokoll&vertrag=${drawer.mietvertrag_nr}`)}>
                Übergabeprotokoll
              </button>
              <button className="me-btn me-btn-outline" title="Wohnungsgeberbescheinigung (§ 19 BMG) erzeugen"
                onClick={() => navigate(`/mietverwaltung?tab=bescheinigungen&doc=wohnungsgeber&vertrag=${drawer.mietvertrag_nr}`)}>
                Wohnungsgeber
              </button>
            </div>
          </div>
        </div>
      )}

      {abgleichModal && (
        <VertragAbgleichModal mietvertragNr={abgleichModal}
          onClose={() => setAbgleichModal(null)}
          onFertig={() => { if (drawer) openDrawer(drawer); loadData(); }} />
      )}

      {auszugModal && (
        <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center",
          justifyContent: "center" }} onClick={() => setAuszugModal(null)}>
          <div style={modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: 0 }}>Auszug erfassen — {auszugModal.mietvertrag_nr}</h3>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: "8px 0 0" }}>
              Setzt das Auszugsdatum auf dem Vertrag — die Einheit gilt danach als Leerstand.
            </p>
            <div style={modalGrid}>
              <label style={modalLabel}>Auszug am *
                <input type="date" style={modalInput} value={auszugModal.auszug}
                  onChange={(e) => setAuszugModal({ ...auszugModal, auszug: e.target.value })} />
              </label>
              <label style={modalLabel}>Gekündigt am
                <input type="date" style={modalInput} value={auszugModal.gekuendigt_am}
                  onChange={(e) => setAuszugModal({ ...auszugModal, gekuendigt_am: e.target.value })} />
              </label>
              <label style={modalLabel}>Kündigung zum
                <input type="date" style={modalInput} value={auszugModal.kuendigung_zum}
                  onChange={(e) => setAuszugModal({ ...auszugModal, kuendigung_zum: e.target.value })} />
              </label>
            </div>
            {aktionFehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{aktionFehler}</p>}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button className="me-btn me-btn-outline" onClick={() => setAuszugModal(null)}>Abbrechen</button>
              <button className="me-btn" disabled={aktionBusy} onClick={submitAuszug}
                style={{ background: "#4f46e5", borderColor: "#4f46e5", color: "#fff" }}>
                {aktionBusy ? "Speichert…" : "Auszug speichern"}
              </button>
            </div>
          </div>
        </div>
      )}

      {folgevertragModal && (
        <FolgevertragModal einheit={folgevertragModal.einheit}
          einheitQm={folgevertragModal.qm}
          titel={folgevertragModal.erstvertrag ? "Neuer Mietvertrag" : "Folgevertrag"}
          onClose={() => setFolgevertragModal(null)}
          onCreated={async (neu) => {
            setFolgevertragModal(null);
            closeDrawer();
            await loadData();
            openDrawer({ mietvertrag_nr: neu.mietvertrag_nr, name: neu.mieter1_name, objekt: neu.objekt, einheit: neu.einheit });
          }} />
      )}

      {kuendigenModal && (
        <KuendigenModal vertrag={kuendigenModal} onClose={() => setKuendigenModal(null)}
          onCreated={(vorgang) => {
            setKuendigenModal(null);
            closeDrawer();
            loadData();
            navigate(`/mietverwaltung?tab=vorgaenge&vorgang=${vorgang.id}`);
          }} />
      )}
    </>
  );
}
